import { BN, web3 } from "@project-serum/anchor";
import {
  deserializeBorsh,
  getProposal,
  Governance,
  GOVERNANCE_SCHEMA,
  ProgramAccount,
  Proposal,
  ProposalState,
  ProposalTransaction,
  VoteRecord,
} from "@solana/spl-governance";
import { AccountInfo, NATIVE_MINT, TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { AnchorWallet } from "@solana/wallet-adapter-react";
import { PublicKey, TransactionInstruction } from "@solana/web3.js";
import dayjs from "dayjs";
import {
  CHAIN_ID,
  TREASURY_INDEX,
} from "../../../common/constants/common.constants";
import { ClubType } from "../../../common/enums/club.enum";
import {
  OfferStatus,
  ProposalAction,
  ProposalTypeForProgram,
  ProposalTypeProgramString,
} from "../../../common/enums/proposals.enums";
import {
  IClubTreasury,
  IMembersRights,
  IReservedRights,
  ISellPermissionConfig,
  ITreasuryTokenAccInfo,
} from "../../../common/interfaces/clubs.interface";
import {
  IBasicProposalData,
  ICancelledProposalData,
  IProposalWithMetaData,
  ProposalTypeNew,
} from "../../../common/interfaces/proposals.interface";
import { canCancel, getTreasuryCount } from "../../../utilities/helpers";
import {
  getProposalByAddress,
  getProposalOfferStatus,
} from "../../methods/proposals";
import {
  acceptOfferSeed,
  escrowSeed,
  ESCRROW_PROGRAM,
  governanceSeed,
  maxVoterWeightSeed,
  profitSeed,
  programFactory,
  RPC_CONNECTION,
  sellPermissionGovernanceSeed,
  splGovernanceProgram,
  transferProfitSeed,
  treasuryDataSeed,
  treasurySeed,
  unqClubMemberSeed,
  unqClubSeed,
  vaultSeed,
} from "../utilities";

import {
  checkIfSellPermissionIsValid,
  createTokenAccountOwnedBySpecifiedAccount,
} from "./common.helpers";
import { getCancellationReasonForProposal } from "../../../api/proposals.api";

import {
  getMagicEdenBuyProposalMetadata,
  getMagicEdenSellProposalMetadata,
  getP2PProposalMetadata,
  getSolseProposalMetadata,
  getUpdateGovernanceConfigProposalMetadata,
  getUpdateRoleConfigProposalMetadata,
  getTransferProposalMetadata,
  getWithdrawalProposalMetadata,
  getSellPermissionProposal,
} from "./proposal-types.helpers";
import { getNftDataFromSolsea } from "../../../api/solsea.api";
import { IInstructionSet } from "../../../common/interfaces/common.interface";

export const getProposalActionFromInstructionAccount = (
  makerOfferedTokenAccountInfo: AccountInfo,
  treasury: string,
  vault: string
) => {
  switch (makerOfferedTokenAccountInfo.owner.toString()) {
    case treasury: {
      return ProposalAction.BuyNft;
    }
    case vault: {
      return ProposalAction.SellNft;
    }
    default: {
      throw new Error("Proposal action undefined");
    }
  }
};
const calculateVotePercentage = (c: BN, total?: BN) => {
  const votePrecision = 10000;
  if (total?.isZero()) {
    return 0;
  }

  return (
    c
      .mul(new BN(votePrecision))
      .div(total ?? new BN(1))
      .toNumber() *
    (100 / votePrecision)
  );
};
export const setupVotingTime = (proposal: Proposal, governance: Governance) => {
  const now = dayjs().unix();

  let timeToVoteEnd = proposal.isPreVotingState()
    ? governance.config.maxVotingTime
    : (proposal.votingAt?.toNumber() ?? 0) +
      governance.config.maxVotingTime -
      now;

  if (timeToVoteEnd <= 0) {
    return null;
  }

  const days = Math.floor(timeToVoteEnd / 86400);
  timeToVoteEnd -= days * 86400;

  const hours = Math.floor(timeToVoteEnd / 3600) % 24;
  timeToVoteEnd -= hours * 3600;

  const minutes = Math.floor(timeToVoteEnd / 60) % 60;
  timeToVoteEnd -= minutes * 60;
  const seconds = Math.floor(timeToVoteEnd % 60);

  return { days, hours, minutes, seconds };
};
export async function getProposalVotes(proposal: IProposalWithMetaData) {
  const clubProgram = programFactory();
  const yesVoteCountV2 =
    proposal.proposalBasicData.proposalAccount.account.options[0].voteWeight;

  const noVoteCountV2 = proposal.proposalBasicData.proposalAccount.account
    .denyVoteWeight
    ? proposal.proposalBasicData.proposalAccount.account.denyVoteWeight.toNumber()
    : 0;

  const [maxVoterWeightPda] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      proposal.proposalBasicData.proposalAccount.pubkey.toBuffer(),
      maxVoterWeightSeed,
    ],
    clubProgram.programId
  );

  let maxVoteWeight =
    proposal.proposalBasicData.proposalMetadata.maxVoterWeight;

  if (!maxVoteWeight) {
    maxVoteWeight = (
      await clubProgram.account.maxVoterWeightRecord.fetch(maxVoterWeightPda)
    ).maxVoterWeight!.toNumber();
  }

  const yesVotePct = calculateVotePercentage(
    new BN(yesVoteCountV2),
    new BN(maxVoteWeight)
  );

  const noVotePct = calculateVotePercentage(
    new BN(noVoteCountV2),
    new BN(maxVoteWeight)
  );

  return {
    yesVotePct: yesVotePct > 100 ? 100 : yesVotePct,
    noVotePct: noVotePct > 100 ? 100 : noVotePct,
    voteThresholdPct: proposal.proposalBasicData.proposalMetadata.voteThreshold,
  };
}

export async function getProposalMetaData(
  proposal: IBasicProposalData,
  realmName: string,
  proposalTransaction: ProposalTransaction,
  wallet: AnchorWallet,
  reservedRights: IReservedRights | undefined,
  denominatedCurrency?: ITreasuryTokenAccInfo | undefined,
  memberRights?: IMembersRights
): Promise<any> {
  //TODO@milica: support multiple transactions and instructions
  const clubProgram = programFactory();
  const [realmAddress] = await web3.PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );
  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmAddress.toBuffer()],
    clubProgram.programId
  );
  const [treasury, trBump] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasurySeed,
      getTreasuryCount(TREASURY_INDEX),
    ],
    clubProgram.programId
  );
  const [treasuryData] = PublicKey.findProgramAddressSync(
    [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
    clubProgram.programId
  );
  const [vaultAddress, vaultBumb] = await PublicKey.findProgramAddress(
    [
      unqClubSeed,
      clubData.toBuffer(),
      treasury.toBuffer(),
      vaultSeed,
      Buffer.from(CHAIN_ID),
    ],
    clubProgram.programId
  );

  const proposalInstruction = proposalTransaction.instructions[0];

  const proposalAction = proposal.proposalMetadata.proposalAction;
  let proposalMetadata;

  switch (proposalAction) {
    case ProposalAction.BuyNft:
    case ProposalAction.SellNft:
      switch (proposal.proposalMetadata.proposalType) {
        case ProposalTypeForProgram.BuyNowMagicEden:
          proposalMetadata = await getMagicEdenBuyProposalMetadata(
            proposalAction,
            vaultBumb,
            trBump,
            proposalInstruction
          );
          break;
        case ProposalTypeForProgram.SellMagicEden:
          proposalMetadata = await getMagicEdenSellProposalMetadata(
            proposalInstruction,
            vaultAddress,
            vaultBumb
          );
          break;
        case ProposalTypeForProgram.BuyP2P:
        case ProposalTypeForProgram.SellP2P:
          proposalMetadata = await getP2PProposalMetadata(
            treasury,
            vaultAddress,
            proposalAction,
            vaultBumb,
            trBump,
            proposalInstruction
          );
          break;
        case ProposalTypeForProgram.BuySolsea:
        case ProposalTypeForProgram.SellSolsea:
          proposalMetadata = await getSolseProposalMetadata(
            proposalAction,
            vaultBumb,
            trBump,
            proposalInstruction
          );
          break;
      }
      break;
    case ProposalAction.Withdrawal:
      proposalMetadata = await getWithdrawalProposalMetadata(
        treasury,
        realmName,
        wallet,
        denominatedCurrency!,
        memberRights!,
        proposal.proposalAccount.pubkey,
        proposalInstruction,
        proposal.proposalMetadata.currentFundraiseIndex,
        proposal.proposalMetadata.createdAt,
        reservedRights
      );
      break;
    case ProposalAction.Transfer:
      proposalMetadata = await getTransferProposalMetadata(proposalInstruction);
      break;
    case ProposalAction.UpdateGovernanceConfig:
      proposalMetadata = await getUpdateGovernanceConfigProposalMetadata(
        proposalTransaction.instructions,
        treasuryData
      );
      break;
    case ProposalAction.UpdateRoleConfig:
      proposalMetadata = await getUpdateRoleConfigProposalMetadata(
        proposalInstruction
      );
      break;
    case ProposalAction.AddSellPermission:
      proposalMetadata = await getSellPermissionProposal(proposalInstruction);
  }

  return {
    ...proposalMetadata,
  };
}
export async function getVoteRecordsByProposalMapByVoter(
  proposalPubKey: PublicKey,
  user: PublicKey,
  realmName: string,
  mint: PublicKey
) {
  const [realmPda] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(realmName)],
    splGovernanceProgram
  );
  const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
    [governanceSeed, realmPda.toBuffer(), mint.toBuffer(), user.toBuffer()],
    splGovernanceProgram
  );

  const [voteRecordAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, proposalPubKey.toBuffer(), tokenOwnerRecord.toBuffer()],
    splGovernanceProgram
  );

  const voteRecordRaw = await RPC_CONNECTION.getAccountInfo(voteRecordAddress);

  if (!voteRecordRaw) {
    return undefined;
  }

  const voteRecord: ProgramAccount<VoteRecord> = {
    account: deserializeBorsh(
      GOVERNANCE_SCHEMA,
      VoteRecord,
      voteRecordRaw?.data!
    ),
    owner: splGovernanceProgram,
    pubkey: voteRecordAddress,
  };
  return voteRecord;
}

export const getAccountGovernanceForTradingProposal = async (
  sellPermissions: ISellPermissionConfig[] | undefined,
  amount,
  realmPda,
  clubProgram,
  treasury: IClubTreasury,
  accountGovernancePda: PublicKey,
  userRole?: string,
  isTradingInDenomCurrency?: boolean
) => {
  const [clubData] = await PublicKey.findProgramAddress(
    [unqClubSeed, realmPda.toBuffer()],
    clubProgram.programId
  );

  if (
    sellPermissions &&
    checkIfSellPermissionIsValid(sellPermissions[0]) &&
    isTradingInDenomCurrency
  ) {
    const proposalSellPermissionIndex = sellPermissions.findIndex(
      (sellPermission) =>
        sellPermission.from <= amount && sellPermission.to > amount
    );
    const countBuff = Buffer.alloc(4);
    if (proposalSellPermissionIndex >= 0) {
      countBuff.writeInt32LE(proposalSellPermissionIndex, 0);
    }

    const [sellPermissionAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        realmPda.toBuffer(),
        sellPermissionGovernanceSeed,
        countBuff,
      ],
      clubProgram.programId
    );

    if (
      sellPermissions &&
      proposalSellPermissionIndex >= 0 &&
      sellPermissions[proposalSellPermissionIndex] &&
      sellPermissions[proposalSellPermissionIndex].governance
    ) {
      return {
        accountGovernancePda:
          sellPermissions[proposalSellPermissionIndex].governance!,
        spcAccount: sellPermissionAddress,
        useDeny: sellPermissions[proposalSellPermissionIndex].votingQuorum > 51,
        isSellPermissionFromZeroAndUserIsFounder:
          sellPermissions[proposalSellPermissionIndex].votingQuorum === 0,
      };
    }
  }
  return {
    accountGovernancePda: accountGovernancePda,
    spcAccount: treasury.treasury,
    useDeny: true,
    isSellPermissionFromZeroAndUserIsFounder: false,
  };
};

export const getEscrowWantedTokensBasedOnProposal = async (
  proposal: IProposalWithMetaData
) => {
  try {
    const [escrowWantedTokenAccount] = await PublicKey.findProgramAddress(
      [escrowSeed, proposal.metadata.offer.toBuffer(), acceptOfferSeed],
      ESCRROW_PROGRAM
    );
    return escrowWantedTokenAccount.toString();
  } catch (error) {
    console.log(error);
    throw error;
  }
};
const canUserCancelProposal = async (
  proposal: IProposalWithMetaData,
  wallet: AnchorWallet,
  clubName: string,
  mint: PublicKey
) => {
  if (
    wallet &&
    proposal &&
    proposal.proposalBasicData.proposalAccount.account.state ===
      ProposalState.Voting
  ) {
    const canCancelFlag = await canCancel(proposal, mint, clubName, wallet!);
    return canCancelFlag;
  } else {
    return false;
  }
};

const getTimeToVoteEnd = (proposal: IProposalWithMetaData) => {
  const timeToVote = setupVotingTime(
    proposal.proposalBasicData.proposalAccount.account,
    proposal.governanceAccount
  );
  return timeToVote;
};

export const getProposalMetadataForContext = async (
  proposal: IBasicProposalData,
  clubName: string,
  mint: PublicKey,
  memberRights: IMembersRights,
  wallet: AnchorWallet,
  clubType: ClubType,
  denominatedCurrency: ITreasuryTokenAccInfo | undefined,
  reservedRights: IReservedRights | undefined,
  existingProposal?: IProposalWithMetaData
) => {
  try {
    let updatedProposalWithMetadta: IProposalWithMetaData;
    if (existingProposal) {
      const updatedSplProposal = await getProposal(
        RPC_CONNECTION,
        proposal.proposalAccount.pubkey
      );
      updatedProposalWithMetadta = {
        ...existingProposal,
        proposalBasicData: { ...proposal, proposalAccount: updatedSplProposal },
      };
    } else {
      const proposalData = await getProposalByAddress(
        proposal,
        clubName,
        wallet!,
        reservedRights,
        denominatedCurrency,
        undefined,
        memberRights
      );
      updatedProposalWithMetadta = { ...proposalData };
    }
    if (
      updatedProposalWithMetadta.metadata.proposalAction ===
        ProposalAction.BuyNft ||
      updatedProposalWithMetadta.metadata.proposalAction ===
        ProposalAction.SellNft
    ) {
      const offer = await getProposalOfferStatus(
        updatedProposalWithMetadta.metadata.offer
      );
      updatedProposalWithMetadta = {
        ...updatedProposalWithMetadta,
        metadata: {
          ...updatedProposalWithMetadta.metadata,
          offerStatus: offer,
        },
      };
    }
    if (
      updatedProposalWithMetadta.proposalBasicData.proposalMetadata
        .proposalType === ProposalTypeForProgram.SellSolsea &&
      updatedProposalWithMetadta.metadata.offerStatus ===
        OfferStatus.listedOnSolsea
    ) {
      try {
        const solseaNftStats = await getNftDataFromSolsea(
          updatedProposalWithMetadta.metadata.offeredTokenMint.toString()
        );

        if (
          !solseaNftStats ||
          !solseaNftStats.listed ||
          (solseaNftStats.sellerKey !==
            updatedProposalWithMetadta.metadata.maker.toString() &&
            solseaNftStats.listed)
        ) {
          updatedProposalWithMetadta = {
            ...updatedProposalWithMetadta,
            metadata: {
              ...updatedProposalWithMetadta.metadata,
              hasNftSoldOnSolsea: true,
            },
          };
        }
      } catch (error) {
        updatedProposalWithMetadta = {
          ...updatedProposalWithMetadta,
          metadata: {
            ...updatedProposalWithMetadta.metadata,
            hasNftSoldOnSolsea: true,
          },
        };
      }
    }
    if (
      updatedProposalWithMetadta.proposalBasicData.proposalAccount.account
        .state === ProposalState.Cancelled ||
      updatedProposalWithMetadta.metadata.offerStatus ===
        OfferStatus.canceled ||
      updatedProposalWithMetadta.metadata.offerStatus ===
        OfferStatus.canceledOnSolsea ||
      updatedProposalWithMetadta.metadata.offerStatus ===
        OfferStatus.canceledOnMagic
    ) {
      try {
        const cancellationStatus: ICancelledProposalData =
          await getCancellationReasonForProposal(
            updatedProposalWithMetadta.proposalBasicData.proposalAccount.pubkey.toString()
          );

        updatedProposalWithMetadta = {
          ...updatedProposalWithMetadta,
          metadata: {
            ...updatedProposalWithMetadta.metadata,
            cancellationReason: cancellationStatus.label,
          },
        };
      } catch (error) {}
    }
    if (!updatedProposalWithMetadta.metadata.userVoteResult) {
      const userVotes = await getVoteRecordsByProposalMapByVoter(
        proposal.proposalAccount.pubkey,
        wallet!.publicKey,
        clubName,
        mint
      );
      updatedProposalWithMetadta = {
        ...updatedProposalWithMetadta,
        metadata: {
          ...updatedProposalWithMetadta.metadata,
          userVoteResult: userVotes,
        },
      };
    }
    const proposalVotes = await getProposalVotes(updatedProposalWithMetadta);
    updatedProposalWithMetadta = {
      ...updatedProposalWithMetadta,
      metadata: {
        ...updatedProposalWithMetadta.metadata,
        proposalVotes: proposalVotes,
      },
    };
    if (
      updatedProposalWithMetadta.proposalBasicData.proposalAccount.account
        .state === ProposalState.Voting
    ) {
      const canUserCancel = await canUserCancelProposal(
        updatedProposalWithMetadta,
        wallet,
        clubName,
        mint
      );
      updatedProposalWithMetadta = {
        ...updatedProposalWithMetadta,
        metadata: { ...updatedProposalWithMetadta.metadata, canUserCancel },
      };
    }

    const timeToVote = getTimeToVoteEnd(updatedProposalWithMetadta);
    updatedProposalWithMetadta = {
      ...updatedProposalWithMetadta,
      metadata: { ...updatedProposalWithMetadta.metadata, timeToVote },
    };
    return updatedProposalWithMetadta;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export function mapProposalTypeFromProgram(
  proposalStatus: ProposalTypeProgramString
) {
  switch (proposalStatus) {
    case ProposalTypeProgramString.BuyNowMagicEden:
      return ProposalTypeForProgram.BuyNowMagicEden;
    case ProposalTypeProgramString.BuyP2P:
      return ProposalTypeForProgram.BuyP2P;
    case ProposalTypeProgramString.BuySolsea:
      return ProposalTypeForProgram.BuySolsea;
    case ProposalTypeProgramString.Discussion:
      return ProposalTypeForProgram.Discussion;
    case ProposalTypeProgramString.SellMagicEden:
      return ProposalTypeForProgram.SellMagicEden;
    case ProposalTypeProgramString.SellP2P:
      return ProposalTypeForProgram.SellP2P;
    case ProposalTypeProgramString.SellSolsea:
      return ProposalTypeForProgram.SellSolsea;
    case ProposalTypeProgramString.TransferFunds:
      return ProposalTypeForProgram.TransferFunds;
    case ProposalTypeProgramString.Withdrawal:
      return ProposalTypeForProgram.Withdrawal;
    case ProposalTypeProgramString.UpdateGovernanceConfig:
      return ProposalTypeForProgram.UpdateGovernanceConfig;
    case ProposalTypeProgramString.UpdateRoleConfig:
      return ProposalTypeForProgram.UpdateRoleConfig;
    case ProposalTypeProgramString.AddSellPermission:
      return ProposalTypeForProgram.AddSellPermission;
  }
}

export function mapProposalAction(proposalType: ProposalTypeForProgram) {
  switch (proposalType) {
    case ProposalTypeForProgram.BuyNowMagicEden:
    case ProposalTypeForProgram.BuyP2P:
    case ProposalTypeForProgram.BuySolsea:
      return ProposalAction.BuyNft;
    case ProposalTypeForProgram.SellMagicEden:
    case ProposalTypeForProgram.SellP2P:
    case ProposalTypeForProgram.SellSolsea:
      return ProposalAction.SellNft;
    case ProposalTypeForProgram.Discussion:
      return ProposalAction.Discussion;
    case ProposalTypeForProgram.Withdrawal:
      return ProposalAction.Withdrawal;
    case ProposalTypeForProgram.TransferFunds:
      return ProposalAction.Transfer;
    case ProposalTypeForProgram.UpdateGovernanceConfig:
      return ProposalAction.UpdateGovernanceConfig;
    case ProposalTypeForProgram.UpdateRoleConfig:
      return ProposalAction.UpdateRoleConfig;
    case ProposalTypeForProgram.AddSellPermission:
      return ProposalAction.AddSellPermission;
  }
}

export const prepareTransferProfitIx = async (
  treasury: PublicKey,
  proposalAddress: PublicKey,
  fungibleMint: PublicKey,
  payer: AnchorWallet,
  transferAmount: number,
  clubData: PublicKey
) => {
  try {
    const clubProgram = programFactory();
    const allInstructions: IInstructionSet[] = [];
    const [profit] = await PublicKey.findProgramAddress(
      [unqClubSeed, treasury.toBuffer(), profitSeed],
      clubProgram.programId
    );
    const [treasuryData] = await PublicKey.findProgramAddress(
      [unqClubSeed, treasury.toBuffer(), treasuryDataSeed],
      clubProgram.programId
    );

    const [memberPDA] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        payer.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );

    let transfeProfitIx: TransactionInstruction | undefined = undefined;
    const [transferProfitAddress] = await PublicKey.findProgramAddress(
      [unqClubSeed, proposalAddress.toBuffer(), transferProfitSeed],
      clubProgram.programId
    );
    const profitTokenAccounts =
      await RPC_CONNECTION.getParsedTokenAccountsByOwner(profit, {
        mint: fungibleMint,
      });

    if (profitTokenAccounts.value.length > 0) {
      const treasuryTokenAccounts =
        await RPC_CONNECTION.getParsedTokenAccountsByOwner(treasury, {
          mint: fungibleMint,
        });

      let treasuryToken: PublicKey | undefined = undefined;

      if (treasuryTokenAccounts.value.length > 0) {
        treasuryToken = treasuryTokenAccounts.value[0].pubkey;
      } else {
        if (fungibleMint.toString() === NATIVE_MINT.toString()) {
          const {
            createAccountIx,
            createInitAccountInstruction,
            tokenAccount,
          } = await createTokenAccountOwnedBySpecifiedAccount(
            payer.publicKey,
            treasury,
            NATIVE_MINT
          );
          treasuryToken = tokenAccount.publicKey;
          allInstructions.push({
            instructions: [createAccountIx, createInitAccountInstruction],
            partialSigner: tokenAccount,
            description: "Create token account",
          });
        } else {
          throw new Error("Missing treasury token account");
        }
      }

      const transferProfitInstruction = clubProgram.instruction.transferProfit(
        new BN(transferAmount),
        {
          accounts: {
            clubData,
            payer: payer.publicKey,
            treasury: treasury,
            treasuryToken: treasuryToken,
            treasuryData: treasuryData,
            profit,
            transferProfit: transferProfitAddress,
            proposal: proposalAddress,
            profitToken: profitTokenAccounts.value[0].pubkey,
            memberData: memberPDA,
            systemProgram: web3.SystemProgram.programId,
            tokenProgram: TOKEN_PROGRAM_ID,
          },
        }
      );

      transfeProfitIx = transferProfitInstruction;
    }

    return {
      allInstructions,
      transfeProfitIx,
    };
  } catch (error) {
    throw error;
  }
};

export const getTradeAndTransferApprovalQuorum = (
  clubTreasury: IClubTreasury,
  isPriceEmpty: boolean,
  price: number,
  isDenom: boolean,
  proposalType: ProposalTypeNew,
  governances: ProgramAccount<Governance>[]
): number => {
  const transferGovernance =
    clubTreasury.transferGovernance &&
    governances.find(
      (item) =>
        item.pubkey.toString() === clubTreasury.transferGovernance?.toString()
    );
  if (
    proposalType === ProposalTypeNew.UpdateClubConfig &&
    clubTreasury.changeApprovalConfigGovernance
  ) {
    return governances.find(
      (g) =>
        g.pubkey.toString() ===
        clubTreasury.changeApprovalConfigGovernance?.toString()
    )?.account.config.voteThresholdPercentage.value!;
  }
  if (isDenom && !isPriceEmpty) {
    const sellPermission = clubTreasury.sellPermissions.find(
      (item) => item.from <= price && item.to > price
    );
    if (sellPermission) {
      return sellPermission.votingQuorum;
    }
  }

  if (proposalType === ProposalTypeNew.Transfer && transferGovernance) {
    return transferGovernance.account.config.voteThresholdPercentage.value;
  }

  const defaultGovernance = governances.find(
    (item) => item.pubkey.toString() === clubTreasury.governance.toString()
  );
  if (!defaultGovernance) {
    throw new Error("Can not find governance for proposal");
  }
  return defaultGovernance.account.config.voteThresholdPercentage.value;
};

export const getMaxVotingTime = (
  treasury,
  governances,
  proposalType: ProposalTypeNew
) => {
  let govKey: PublicKey | undefined;
  switch (proposalType) {
    case ProposalTypeNew.Transfer: {
      govKey = treasury.transferGovernance;
      break;
    }
    case ProposalTypeNew.UpdateClubConfig: {
      govKey = treasury.changeApprovalConfigGovernance;
      break;
    }
    case ProposalTypeNew.Withdrawal: {
      govKey = treasury.withdrawalGovernance;
      break;
    }

    default:
      govKey = treasury.governance;
      break;
  }

  const govAcc =
    govKey &&
    governances.find((el) => el.pubkey.toString() === govKey?.toString());

  return ((govAcc.account.config.maxVotingTime - 1) / 60 / 60 / 24).toFixed();
};
